/**
 * Database Path Utilities
 * Builds collection paths with tenant/store structure
 */

import { getTenantId, getStoreId } from '../services/userStoreCache';
import { DEFAULT_UNAUTH_TENANT_PATH } from '../localTenantConfig';

/**
 * Build collection path with tenant/store prefix
 * @param {string} collectionName - Base collection name (e.g., 'bookings', 'products1')
 * @returns {string} Full path (e.g., 'tenants/1234/stores/999/bookings')
 */
export const getCollectionPath = (collectionName) => {
  const rawTenantId = getTenantId();
  const rawStoreId = getStoreId();

  // Treat string "null"/"undefined" as missing context (can happen when reading from localStorage)
  const tenantId =
    rawTenantId && rawTenantId !== "null" && rawTenantId !== "undefined"
      ? rawTenantId
      : null;
  const storeId =
    rawStoreId && rawStoreId !== "null" && rawStoreId !== "undefined"
      ? rawStoreId
      : null;
  
  // If no tenant/store context, use default unauthenticated tenant/store path
  if (!tenantId || !storeId) {
    if (DEFAULT_UNAUTH_TENANT_PATH) {
      const path = `${DEFAULT_UNAUTH_TENANT_PATH}/${collectionName}`;
      return path;
    }
    return collectionName;
  }
  
  const path = `tenants/${tenantId}/stores/${storeId}/${collectionName}`;
  return path;
};

/**
 * Build settings document path with tenant/store prefix
 * @param {string} settingName - Setting document name (e.g., 'salesTax', 'storeName')
 * @returns {string} Full path (e.g., 'tenants/1234/stores/999/settings')
 */
export const getSettingsPath = () => {
  const rawTenantId = getTenantId();
  const rawStoreId = getStoreId();

  const tenantId =
    rawTenantId && rawTenantId !== "null" && rawTenantId !== "undefined"
      ? rawTenantId
      : null;
  const storeId =
    rawStoreId && rawStoreId !== "null" && rawStoreId !== "undefined"
      ? rawStoreId
      : null;
  
  // If no tenant/store context, use default unauthenticated tenant/store settings
  if (!tenantId || !storeId) {
    if (DEFAULT_UNAUTH_TENANT_PATH) {
      const path = `${DEFAULT_UNAUTH_TENANT_PATH}/settings`;
      return path;
    }
    return 'settings';
  }
  
  const path = `tenants/${tenantId}/stores/${storeId}/settings`;
  return path;
};

/**
 * Check if tenant/store context is available
 * @returns {boolean} True if context is available
 */
export const hasTenantStoreContext = () => {
  const tenantId = getTenantId();
  const storeId = getStoreId();
  return !!(tenantId && storeId);
};

/**
 * Get tenant/store context for display/debugging
 * @returns {Object} Context object
 */
export const getTenantStoreContext = () => {
  return {
    tenantId: getTenantId(),
    storeId: getStoreId(),
    hasContext: hasTenantStoreContext(),
  };
};


